import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function DocumentationPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const contentRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'architecture', label: 'Architecture' },
    { id: 'query-browser', label: 'Query Browser' },
    { id: 'locations', label: 'Locations' },
    { id: 'data-variables', label: 'Data & Variables' },
    { id: 'faq', label: 'FAQ & Troubleshooting' }
  ];
  
  // Handle initial hash on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the '#'
    if (hash && sections.some(s => s.id === hash)) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToSection(hash, false);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Track scroll position and update active section
  useEffect(() => {
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      
      const scrollPosition = window.scrollY + 100;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          // Update URL hash without scrolling
          if (window.location.hash !== `#${sections[i].id}`) {
            window.history.replaceState(null, '', `#${sections[i].id}`);
          }
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const scrollToSection = (sectionId, updateHash = true) => {
    const element = document.getElementById(sectionId);
    if (element) {
      isScrollingRef.current = true;
      
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Update URL hash
      if (updateHash) {
        window.history.pushState(null, '', `#${sectionId}`);
      }
      
      setActiveSection(sectionId);
      
      // Reset scrolling flag after animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    }
  };
  
  return (
    <div className="flex max-w-[1400px] mx-auto">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto border-r bg-gray-50 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Documentation</h3>
        <nav className="space-y-1">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                activeSection === section.id
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>
        
        <div className="mt-8 pt-8 border-t">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Links</h4>
          <div className="space-y-2">
            <Link to="/locations" className="block text-sm text-blue-600 hover:underline">→ Locations</Link>
            <Link to="/query-browser" className="block text-sm text-blue-600 hover:underline">→ Data Browser</Link>
            <Link to="/devices" className="block text-sm text-blue-600 hover:underline">→ Devices</Link>
            <a href="https://data.crocus-micronet.org/docs" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline">
              → API Docs ↗
            </a>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main ref={contentRef} className="flex-1 px-8 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">CROCUS Portal Documentation</h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Comprehensive guide to the CROCUS Urban Climate Research Portal. Learn how to access environmental 
            sensor data and explore our monitoring sites.
          </p>
        </div>
        
        {/* Overview */}
        <section id="overview" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Overview</h2>
          
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">About CROCUS</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The CROCUS (Collaborative Research on Urban Climate and Sustainability) project is a comprehensive 
              environmental monitoring initiative focused on understanding urban climate dynamics in the Chicago 
              region. Our network of sensors collects real-time data on temperature, humidity, soil moisture, 
              radiation, and tree water use across multiple sites.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Portal Capabilities</h3>
            <ul className="space-y-3 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span><strong>Interactive Maps:</strong> Visualize all monitoring locations with real-time device status</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span><strong>Data Query Browser:</strong> Search, filter, and download environmental data with flexible time ranges</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span><strong>Device Monitoring:</strong> Track sensor health, battery levels, and specifications</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span><strong>Data Visualization:</strong> View time-series charts, histograms, and availability timelines</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span><strong>RESTful API:</strong> Programmatic access for automated data retrieval and integration</span>
              </li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Sensor Types</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">MFR Nodes</h4>
                <p className="text-sm text-gray-700">Multi-function research nodes measuring air temperature, humidity, soil moisture, radiation, and heat flux</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Sap Flow Sensors</h4>
                <p className="text-sm text-gray-700">Tree-mounted sensors measuring sap flow rates and transpiration using the Heat Ratio Method</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Band Dendrometers</h4>
                <p className="text-sm text-gray-700">Precision instruments measuring tree circumference changes and growth patterns in real-time</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">WXT536 Weather Sensors</h4>
                <p className="text-sm text-gray-700">Vaisala multi-parameter weather sensors measuring temperature, humidity, pressure, wind speed/direction, and precipitation</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">AQT530 Air Quality Sensors</h4>
                <p className="text-sm text-gray-700">Vaisala air quality sensors measuring gas concentrations (CO, NO, NO₂, O₃) and particulate matter (PM1, PM2.5, PM10)</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Getting Started */}
        <section id="getting-started" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Getting Started</h2>
          
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Quick Start Guide</h3>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-gray-900 mb-4">5-Minute Walkthrough</h4>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="font-bold text-blue-600 mr-3">1.</span>
                  <span>Visit the <Link to="/locations" className="text-blue-600 hover:underline">Locations</Link> page to explore monitoring sites on an interactive map</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-blue-600 mr-3">2.</span>
                  <span>Click any location to view site details, device status, and recent measurements</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-blue-600 mr-3">3.</span>
                  <span>Navigate to the <Link to="/query-browser" className="text-blue-600 hover:underline">Data Browser</Link> to query sensor data</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-blue-600 mr-3">4.</span>
                  <span>Select filters (location, variable, time range) and click "Run query"</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-blue-600 mr-3">5.</span>
                  <span>View results in charts and tables, download as CSV, or copy API code snippets</span>
                </li>
              </ol>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Navigation</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Locations:</strong> Browse monitoring sites and view device deployments</li>
              <li><strong>Data:</strong> Query, visualize, and download sensor measurements</li>
              <li><strong>Devices:</strong> Explore sensor specifications and deployment details</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Timezone Settings</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Use the timezone selector in the header to display all timestamps in your preferred timezone. 
              The portal defaults to your browser's timezone but supports any IANA timezone identifier. 
              All data is stored in UTC to ensure consistency.
            </p>
          </div>
        </section>
        
        {/* Architecture */}
        <section id="architecture" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Network Architecture</h2>
          
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              The CROCUS sensor network is built on top of the{' '}
              <a href="https://sagecontinuum.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Sage Continuum
              </a>{' '}
              cyberinfrastructure, which provides coordinating hardware and software services enabling AI at the edge. 
              Our LoRaWAN sensors connect to Waggle edge nodes, which then communicate with the Beehive cloud services 
              for data storage and access.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">High-Level Infrastructure</h3>
            
            <div className="border border-gray-300 rounded-lg p-6 mb-4 flex justify-center" style={{backgroundColor: '#ffffff'}}>
              <a href="https://sagecontinuum.org/docs/about/architecture" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://sagecontinuum.org/assets/images/arch_high_01-770507cc9c88a2a5ec6125510fb8e527.svg" 
                  alt="Sage Continuum High-Level Architecture" 
                  className="max-w-full h-auto"
                />
              </a>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Diagram:</strong> High-level Node & Beehive relationship in the Sage Continuum architecture.{' '}
              <a href="https://sagecontinuum.org/docs/about/architecture" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                View full documentation →
              </a>
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              The cyberinfrastructure consists of two main components: <strong>Nodes</strong> that exist at the edge, 
              and cloud services (<strong>Beehive</strong> and <strong>Beekeeper</strong>) that facilitate running 
              science goals and managing the edge devices.
            </p>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Beehive</h4>
                <p className="text-sm text-gray-700">
                  The Node-to-Beehive connection is the pipeline for science. Instructions for the nodes are sent over 
                  this connection, and data is published from edge applications (plugins) running on the nodes into 
                  Beehive storage systems. Each node is associated with a single Beehive, creating isolated "projects" 
                  with their own data stores and web services.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Beekeeper</h4>
                <p className="text-sm text-gray-700">
                  The Beekeeper is an administrative server that allows system administrators to perform actions on the 
                  nodes such as gathering health metrics and performing software updates. All nodes "phone home" to 
                  their Beekeeper and maintain this "life-line" connection.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Wild Waggle Nodes</h4>
                <p className="text-sm text-gray-700">
                  CROCUS uses Wild Waggle Nodes — custom-built weather-proof enclosures intended for remote outdoor 
                  installation. These nodes feature an NVidia Xavier NX ARM64 processor with 8GB shared CPU/GPU RAM, 
                  1TB NVMe storage, and expansion ports for sensors via PoE, USB, and embedded protocols.
                </p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">LoRaWAN Integration</h3>
            
            <div className="border border-gray-300 rounded-lg p-6 mb-4 flex justify-center" style={{backgroundColor: '#ffffff'}}>
              <a href="https://sagecontinuum.org/docs/about/architecture#lorawan" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://sagecontinuum.org/assets/images/abs_arch_wes_lorawan-06a36c092541429f1953e09fcd3aee89.svg" 
                  alt="Sage Continuum LoRaWAN Architecture" 
                  className="max-w-full h-auto"
                />
              </a>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Diagram:</strong> Abstracted WES LoRaWAN Architecture showing how CROCUS sensors connect to Waggle nodes.{' '}
              <a href="https://sagecontinuum.org/docs/reference-guides/lorawan" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                View LoRaWAN reference guide →
              </a>
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              The Waggle Edge Stack (WES) includes the{' '}
              <a href="https://www.chirpstack.io/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                ChirpStack
              </a>{' '}
              software stack and services to facilitate communication between Nodes and LoRaWAN devices. This empowers 
              nodes to establish connections with wireless sensors like our MFR Nodes, Sap Flow Meters, and Band Dendrometers. WXT536 and AQT530 sensors connect via the Sage Continuum platform.
            </p>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">ChirpStack Network Server</h4>
                <p className="text-sm text-gray-700">
                  Manages LoRaWAN devices, handles packet routing, device sessions, and application integrations. 
                  Uses Redis for session caching and PostgreSQL for persistent storage.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">LoRaWAN Listener Plugin</h4>
                <p className="text-sm text-gray-700">
                  A Waggle plugin that publishes measurements from LoRaWAN devices to the Beehive data repository. 
                  This is how CROCUS sensor data flows from field devices to the cloud storage.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">LoRaWAN Gateway</h4>
                <p className="text-sm text-gray-700">
                  Hardware device that receives wireless data from LoRaWAN sensors (up to 2-15km range depending on 
                  environment) and forwards it to the node for processing. CROCUS operates on US915 sub-band 2 
                  (903.9-905.3 MHz).
                </p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">CROCUS Data Pipeline</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The CROCUS portal synchronizes data from the Sage Continuum infrastructure to our own InfluxDB instance, 
              providing a dedicated interface for urban climate research:
            </p>
            <ol className="space-y-2 text-gray-700 mb-4">
              <li><strong>1. Sensor Measurement:</strong> Field devices sample environmental data (15-30 minute intervals)</li>
              <li><strong>2. LoRa Transmission:</strong> Encrypted packets sent to nearest Waggle node via LoRaWAN</li>
              <li><strong>3. Edge Processing:</strong> ChirpStack decodes packets; LoRaWAN Listener plugin publishes to Beehive</li>
              <li><strong>4. Sage Data API:</strong> Data accessible via sage_data_client Python library</li>
              <li><strong>5. CROCUS Sync:</strong> Periodic sync service fetches new data and writes to local InfluxDB</li>
              <li><strong>6. Portal API:</strong> FastAPI serves data via REST endpoints</li>
              <li><strong>7. User Interface:</strong> React portal displays data with charts, maps, and tables</li>
            </ol>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-0">
                <strong>📚 Learn More:</strong> For detailed technical documentation about the Sage Continuum 
                architecture, visit the{' '}
                <a href="https://sagecontinuum.org/docs/about/architecture" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Sage Architecture Documentation
                </a>{' '}
                and{' '}
                <a href="https://sagecontinuum.org/docs/reference-guides/lorawan" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  LoRaWAN Reference Guide
                </a>.
              </p>
            </div>
          </div>
          
          {/* ============================================================================
              PREVIOUS ARCHITECTURE SECTION (Commented Out)
              This was the original architecture documentation before switching to 
              Sage Continuum-based architecture.
              ============================================================================
          
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              The CROCUS network utilizes LoRaWAN (Long Range Wide Area Network) technology to enable 
              low-power, long-range communication between field sensors and the cloud infrastructure. 
              This architecture provides reliable data transmission across urban environments while 
              minimizing power consumption for battery-operated sensors.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">System Overview</h3>
            
            <div className="border border-gray-300 rounded-lg p-6 mb-6 flex justify-center" style={{backgroundColor: '#ffffff'}}>
              <img 
                src="/Network_Architecture.png" 
                alt="CROCUS LoRaWAN Network Architecture" 
                className="max-w-full h-auto"
              />
            </div>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Diagram:</strong> Full CROCUS network architecture showing data flow from field sensors 
              through LoRaWAN gateways to cloud infrastructure and user interface.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Component Details</h3>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">1. Field Sensors</h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4">
                  <li><strong>MFR Nodes:</strong> Multi-function research nodes with environmental sensors (air temp/humidity, soil moisture, radiation, heat flux)</li>
                  <li><strong>Sap Flow Meters:</strong> Heat Ratio Method sensors measuring tree water use (inner/outer sapwood)</li>
                  <li><strong>Band Dendrometers:</strong> Precision circumference bands measuring tree growth and water status</li>
                  <li><strong>WXT536 Weather Sensors:</strong> Vaisala multi-parameter weather sensors (temp, humidity, pressure, wind, precipitation)</li>
                  <li><strong>AQT530 Air Quality Sensors:</strong> Vaisala air quality sensors (CO, NO, NO₂, O₃, PM1, PM2.5, PM10)</li>
                  <li><strong>Communication:</strong> LoRaWAN (US915 FSB2, 903.9-905.3 MHz) with ~3km range in urban areas</li>
                  <li><strong>Power:</strong> Solar-powered with battery backup</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">2. LoRaWAN Gateways</h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4">
                  <li><strong>Model:</strong> RAK Wireless RAK7289CV2 WisGate Edge Pro</li>
                  <li><strong>Channels:</strong> 8-channel concentrator (SX1302 chipset)</li>
                  <li><strong>Backhaul:</strong> LTE Cat4 cellular connectivity</li>
                  <li><strong>Deployment:</strong> One gateway per site (6 total), rooftop/pole mounted</li>
                  <li><strong>Coverage:</strong> ~5-10km radius per gateway in urban environment</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">3. Network Server (The Things Stack)</h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4">
                  <li><strong>Function:</strong> LoRaWAN packet routing, device management, encryption</li>
                  <li><strong>Security:</strong> AES-128 encryption, per-device session keys</li>
                  <li><strong>Decoding:</strong> Custom JavaScript decoders for each device type</li>
                  <li><strong>Integration:</strong> MQTT broker (TLS 1.3, port 8883) for data streaming</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">4. Data Infrastructure</h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4">
                  <li><strong>MQTT Subscriber:</strong> Python service ingesting real-time sensor data</li>
                  <li><strong>InfluxDB:</strong> Time-series database storing all-time data</li>
                  <li><strong>FastAPI:</strong> RESTful API providing HTTP access to data</li>
                  <li><strong>React Portal:</strong> Public web interface (this site) with interactive maps and queries</li>
                </ul>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Data Flow</h3>
            <ol className="space-y-2 text-gray-700 mb-4">
              <li><strong>1. Sensor Measurement:</strong> Devices sample environmental data (15-30 minute intervals)</li>
              <li><strong>2. LoRa Transmission:</strong> Encrypted packets sent to nearest gateway via LoRaWAN</li>
              <li><strong>3. Gateway Uplink:</strong> Gateway forwards packets using UDP Packet Forwarder to The Things Stack via LTE</li>
              <li><strong>4. Network Processing:</strong> Packets decrypted, decoded, and published to MQTT</li>
              <li><strong>5. Database Ingestion:</strong> MQTT subscriber writes data to InfluxDB</li>
              <li><strong>6. API Access:</strong> FastAPI queries InfluxDB and serves data via HTTP</li>
              <li><strong>7. User Interface:</strong> React portal displays data with charts, maps, and tables</li>
            </ol>
          </div>
          
          ============================================================================ */}
        </section>
        
        {/* Query Browser */}
        <section id="query-browser" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Query Browser</h2>
          
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              The Query Browser is a powerful interface for searching, filtering, and visualizing environmental 
              sensor data from all CROCUS monitoring sites.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Time Range Selection</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Quick Ranges:</strong> Choose preset ranges from "last minute" to "last 90 days"</li>
              <li><strong>Custom Range:</strong> Use the calendar picker for specific start/end dates and times</li>
              <li><strong>Timezone:</strong> All timestamps display in your selected timezone (configurable in header)</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Time Averaging</h3>
            <p className="text-gray-700 mb-3">
              Use the <strong>Averaging</strong> dropdown (located to the left of Quick Ranges) to aggregate raw data 
              into time-averaged intervals. This is useful for smoothing noisy data, reducing large datasets, and 
              identifying trends over longer periods.
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Raw data:</strong> Display all individual data points as recorded (default)</li>
              <li><strong>15 min avg:</strong> Average values into 15-minute time buckets</li>
              <li><strong>30 min avg:</strong> Average values into 30-minute time buckets</li>
              <li><strong>1 hour avg:</strong> Average values into 1-hour time buckets</li>
              <li><strong>3 hour avg:</strong> Average values into 3-hour time buckets</li>
              <li><strong>6 hour avg:</strong> Average values into 6-hour time buckets</li>
              <li><strong>1 day avg:</strong> Average values into daily time buckets</li>
            </ul>
            <p className="text-gray-700 mb-3">
              Averaging is computed per variable, location, and device combination. When an averaging interval 
              is selected, both the timeseries chart and the data table update to show averaged values.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-0">
                <strong>💡 Tip:</strong> When averaging is active, the table displays additional statistics columns: 
                <strong> Count</strong> (number of raw data points per bucket), <strong>Min</strong>, <strong>Max</strong>, 
                and <strong>Std</strong> (standard deviation). An info banner shows how many raw records were condensed 
                into averaged records. CSV downloads also include these extra columns.
              </p>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Filters (Multi-Select)</h3>
            <p className="text-gray-700 mb-3">All filter fields support multiple selections. Click and select multiple values from each dropdown:</p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Location:</strong> Filter by monitoring site (BIG, CCICS, CSU, HUM, IBP, NEIU, NU, SHEDD, UIC)</li>
              <li><strong>Variable:</strong> Select measurements like air_temperature, relative_humidity, wxt.env.temp, aqt.gas.ozone</li>
              <li><strong>Device Profile:</strong> Filter by MFR Node, Sap Flow Sensor, Band Dendrometer, WXT536 Sensor, or AQT530 Sensor</li>
              <li><strong>Device ID:</strong> Filter by specific device serial numbers</li>
              <li><strong>Site ID:</strong> Filter by site name within a location</li>
              <li><strong>Tree Species:</strong> Filter sap flow data by tree type</li>
              <li><strong>Sensor Name:</strong> Filter MFR node data by attached sensor</li>
            </ul>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-0">
                <strong>💡 Tip:</strong> Select multiple locations or variables to compare data across sites 
                or measurements in a single query.
              </p>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Visualizations</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Time Series:</strong> Interactive charts with zoom and pan (drag to zoom, Ctrl+drag to pan)</li>
              <li><strong>Frequency:</strong> Bar chart showing data point counts by variable</li>
              <li><strong>Sum:</strong> Aggregated values across time ranges</li>
              <li><strong>Histogram:</strong> Distribution analysis by value, hour, day, or month</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Data Table Options</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Relative Time:</strong> Display timestamps as "2 hours ago" vs absolute dates</li>
              <li><strong>Show Meta:</strong> Display all metadata fields (device, location, units)</li>
              <li><strong>Show Unit:</strong> Include measurement units in value column</li>
              <li><strong>Rows Per Page:</strong> Select 10-100 rows per page</li>
              <li><strong>Averaging Columns:</strong> When a time-averaging interval is active, the table automatically shows Count, Min, Max, and Std columns alongside the averaged Value</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Exporting Data</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>CSV Download:</strong> Click download icon to export current results</li>
              <li><strong>cURL Command:</strong> Copy command to reproduce query in terminal</li>
              <li><strong>Python Snippet:</strong> Copy Python code for API integration</li>
              <li><strong>Citation:</strong> Auto-generated citation text for academic use</li>
            </ul>
          </div>
        </section>
        
        {/* Locations */}
        <section id="locations" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Locations</h2>
          
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              The Locations page displays all CROCUS monitoring sites across the Chicago region with 
              interactive maps and device information.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Interactive Map</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Status Indicators:</strong> Color-coded markers show site health
                <ul className="ml-6 mt-2 space-y-1">
                  <li>🟢 Green: All devices online and reporting</li>
                  <li>🔴 Red: Devices offline or not reporting</li>
                </ul>
              </li>
              <li><strong>Click Markers:</strong> View location details in popup (GPS, status, device list)</li>
              <li><strong>Map Controls:</strong> Pan, zoom, and toggle fullscreen view</li>
              <li><strong>Base Maps:</strong> Switch between Light, Satellite, and Terrain views</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Location Details Page</h3>
            <p className="text-gray-700 mb-3">Click any location name to access its dedicated page featuring:</p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Overview:</strong> Site type, GPS coordinates, registration date</li>
              <li><strong>Device Map:</strong> Precise sensor positions with shape-coded markers (circles, diamonds, squares)</li>
              <li><strong>Device Table:</strong> Status, battery levels, last seen timestamps, and specifications</li>
              <li><strong>Data Timeline:</strong> 7-day availability heatmap showing hourly or daily data density</li>
              <li><strong>Sensor Readings:</strong> Current measurements with sparkline charts showing last 24 hours</li>
              <li><strong>Site Photos:</strong> Visual documentation when available</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Map Legend</h3>
            <p className="text-gray-700 mb-2"><strong>Device Symbols:</strong></p>
            <ul className="text-sm text-gray-700 space-y-1 mb-4">
              <li>● Circle - Sap Flow Sensor</li>
              <li>■ Square - MFR Node</li>
              <li>◆ Diamond - Band Dendrometer</li>
              <li>▲ Triangle - WXT536 Weather Sensor</li>
              <li>⬡ Hexagon - AQT530 Air Quality Sensor</li>
            </ul>
          </div>
        </section>
        
        {/* Data & Variables */}
        <section id="data-variables" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">Data & Variables</h2>
          
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Variable Naming</h3>
            <p className="text-gray-700 mb-4">
              All measurements follow standardized naming conventions. Click any variable name in the 
              Data Browser to view its ontology page with detailed specifications, units, range, and accuracy.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Data Quality</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li><strong>Measurement Frequency:</strong> Most sensors record every 15-30 minutes</li>
              <li><strong>Timestamps:</strong> All data stored in UTC; display timezone configurable</li>
              <li><strong>Missing Data:</strong> Gaps may occur due to maintenance, power, or connectivity issues</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Metadata Fields</h3>
            <p className="text-gray-700 mb-2">Each record includes metadata:</p>
            <ul className="text-sm text-gray-700 space-y-1 mb-4">
              <li><code>location</code> - Monitoring site identifier (BIG, CSU, etc.)</li>
              <li><code>DeviceID</code> - Unique device serial number</li>
              <li><code>DeviceProfileName</code> - Device model/type</li>
              <li><code>DevEUI</code> - LoRaWAN device identifier</li>
              <li><code>SiteID</code> - Specific site within location</li>
              <li><code>TreeSpecies</code> - For sap flow measurements</li>
              <li><code>TreeDiameter</code> - DBH in cm</li>
              <li><code>Unit</code> - Measurement unit</li>
            </ul>
          </div>
        </section>
        
        {/* FAQ & Troubleshooting */}
        <section id="faq" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b">FAQ & Troubleshooting</h2>
          
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Frequently Asked Questions</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Why is there missing data?</h4>
                <p className="text-gray-700">
                  Data gaps can occur due to device maintenance, power issues, network connectivity problems, 
                  or sensor calibration periods. Check device status on the location detail page. Most gaps 
                  are temporary and data is backfilled once connectivity is restored.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">How often is data updated?</h4>
                <p className="text-gray-700">
                  Most sensors report every 15-30 minutes. MFR Nodes typically report every 30 minutes, Sap Flow Meters report every 15-20 minutes, Band Dendrometers report every 15 minutes, and WXT536/AQT530 sensors report every 10 minutes.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Can I download historical data?</h4>
                <p className="text-gray-700">
                  Yes! Use the Query Browser with custom date ranges to access historical data. For very 
                  large downloads, consider using the API directly or requesting data in smaller time chunks. 
                  The API supports queries up to several years of historical data.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What timezone are timestamps in?</h4>
                <p className="text-gray-700">
                  All data is stored in UTC. You can select your preferred timezone in the header, which 
                  will convert timestamps for display and download. The timezone parameter in API queries 
                  affects both input times and output timestamps.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">How do I cite CROCUS data?</h4>
                <p className="text-gray-700">
                  Use the auto-generated citation snippet in the Query Browser, or cite as: "CROCUS Urban 
                  Climate Research Project. [Year]. Environmental monitoring data from [location]. University 
                  of Illinois Chicago. Funded by the U.S. Department of Energy's Office of Science. Accessed 
                  [date] from https://portal.crocus-urban.org"
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Is there an API rate limit?</h4>
                <p className="text-gray-700">
                  No authentication or rate limits are enforced for read-only access. However, please 
                  respect reasonable request rates and avoid rapid polling. For large-scale data collection, 
                  consider using the tail parameter or requesting data in chunks.
                </p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-10 mb-3">Performance Tips</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li><strong>Large Queries:</strong> Queries spanning &gt;30 days may take longer. Use tail parameter to limit results</li>
              <li><strong>Chart Performance:</strong> Charts automatically downsample data above 10,000 points using LTTB algorithm</li>
              <li><strong>Browser Cache:</strong> Clear browser cache if experiencing display issues</li>
              <li><strong>CSV Exports:</strong> Large CSV files may take time to generate. Be patient or reduce time range</li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}